/**
 * In-Memory implementation of SchemaAdapter.
 *
 * Stores schema metadata in memory. Useful for:
 * - Unit tests without database
 * - Mocking schema operations
 * - Testing migration logic
 */

import type {
  SchemaAdapter,
  SchemaTableBuilder,
  SchemaAlterTableBuilder,
  SchemaColumnBuilder,
  SchemaColumnInfo,
  SchemaForeignKeyBuilder
} from '@gzl10/nexus-sdk'

/**
 * In-memory column metadata
 */
interface MemoryColumnMeta {
  name: string
  type: string
  nullable: boolean
  defaultValue: unknown
  primary: boolean
  unique: boolean
  index: boolean
  references?: { column: string; table: string; onDelete?: string }
}

/**
 * In-memory table metadata
 */
interface MemoryTableMeta {
  name: string
  columns: Map<string, MemoryColumnMeta>
}

/**
 * In-memory column builder
 */
class MemoryColumnBuilder implements SchemaColumnBuilder {
  constructor(private meta: MemoryColumnMeta) {}

  primary(): SchemaColumnBuilder {
    this.meta.primary = true
    return this
  }

  unique(): SchemaColumnBuilder {
    this.meta.unique = true
    return this
  }

  index(): SchemaColumnBuilder {
    this.meta.index = true
    return this
  }

  nullable(): SchemaColumnBuilder {
    this.meta.nullable = true
    return this
  }

  notNullable(): SchemaColumnBuilder {
    this.meta.nullable = false
    return this
  }

  defaultTo(value: unknown): SchemaColumnBuilder {
    this.meta.defaultValue = value
    return this
  }

  references(column: string): SchemaColumnBuilder {
    this.meta.references = { column, table: '' }
    return this
  }

  inTable(table: string): SchemaColumnBuilder {
    if (this.meta.references) {
      this.meta.references.table = table
    }
    return this
  }

  onDelete(action: 'CASCADE' | 'SET NULL' | 'RESTRICT' | 'NO ACTION'): SchemaColumnBuilder {
    if (this.meta.references) {
      this.meta.references.onDelete = action
    }
    return this
  }
}

/**
 * In-memory foreign key builder (no-op for testing)
 */
class MemoryForeignKeyBuilder implements SchemaForeignKeyBuilder {
  references(_column: string): SchemaForeignKeyBuilder {
    return this
  }

  inTable(_table: string): SchemaForeignKeyBuilder {
    return this
  }

  onDelete(_action: 'CASCADE' | 'SET NULL' | 'RESTRICT' | 'NO ACTION'): SchemaForeignKeyBuilder {
    return this
  }

  onUpdate(_action: 'CASCADE' | 'SET NULL' | 'RESTRICT' | 'NO ACTION'): SchemaForeignKeyBuilder {
    return this
  }
}

/**
 * In-memory table builder
 */
class MemoryTableBuilder implements SchemaTableBuilder {
  constructor(protected tableMeta: MemoryTableMeta) {}

  private createColumnMeta(name: string, type: string): MemoryColumnMeta {
    const meta: MemoryColumnMeta = {
      name,
      type,
      nullable: true,
      defaultValue: undefined,
      primary: false,
      unique: false,
      index: false
    }
    this.tableMeta.columns.set(name, meta)
    return meta
  }

  string(name: string, _size?: number): SchemaColumnBuilder {
    return new MemoryColumnBuilder(this.createColumnMeta(name, 'string'))
  }

  text(name: string): SchemaColumnBuilder {
    return new MemoryColumnBuilder(this.createColumnMeta(name, 'text'))
  }

  integer(name: string): SchemaColumnBuilder {
    return new MemoryColumnBuilder(this.createColumnMeta(name, 'integer'))
  }

  decimal(name: string, _precision?: number, _scale?: number): SchemaColumnBuilder {
    return new MemoryColumnBuilder(this.createColumnMeta(name, 'decimal'))
  }

  boolean(name: string): SchemaColumnBuilder {
    return new MemoryColumnBuilder(this.createColumnMeta(name, 'boolean'))
  }

  date(name: string): SchemaColumnBuilder {
    return new MemoryColumnBuilder(this.createColumnMeta(name, 'date'))
  }

  datetime(name: string): SchemaColumnBuilder {
    return new MemoryColumnBuilder(this.createColumnMeta(name, 'datetime'))
  }

  json(name: string): SchemaColumnBuilder {
    return new MemoryColumnBuilder(this.createColumnMeta(name, 'json'))
  }

  uuid(name: string): SchemaColumnBuilder {
    return new MemoryColumnBuilder(this.createColumnMeta(name, 'uuid'))
  }

  bigInteger(name: string): SchemaColumnBuilder {
    return new MemoryColumnBuilder(this.createColumnMeta(name, 'bigInteger'))
  }

  timestamp(name: string): SchemaColumnBuilder {
    return new MemoryColumnBuilder(this.createColumnMeta(name, 'timestamp'))
  }

  jsonb(name: string): SchemaColumnBuilder {
    return new MemoryColumnBuilder(this.createColumnMeta(name, 'jsonb'))
  }

  enum(name: string, _values: readonly string[]): SchemaColumnBuilder {
    return new MemoryColumnBuilder(this.createColumnMeta(name, 'enum'))
  }

  binary(name: string, _length?: number): SchemaColumnBuilder {
    return new MemoryColumnBuilder(this.createColumnMeta(name, 'binary'))
  }

  float(name: string, _precision?: number, _scale?: number): SchemaColumnBuilder {
    return new MemoryColumnBuilder(this.createColumnMeta(name, 'float'))
  }

  specificType(name: string, type: string): SchemaColumnBuilder {
    return new MemoryColumnBuilder(this.createColumnMeta(name, type))
  }

  index(_columns: string | string[], _indexName?: string): void {
    // No-op for in-memory adapter
  }

  unique(_columns: string | string[], _constraintName?: string): void {
    // No-op for in-memory adapter
  }

  primary(_columns: string | string[], _constraintName?: string): void {
    // No-op for in-memory adapter
  }

  foreign(_column: string): SchemaForeignKeyBuilder {
    return new MemoryForeignKeyBuilder()
  }

  increments(name?: string): SchemaColumnBuilder {
    return new MemoryColumnBuilder(this.createColumnMeta(name ?? 'id', 'increments'))
  }

  bigIncrements(name?: string): SchemaColumnBuilder {
    return new MemoryColumnBuilder(this.createColumnMeta(name ?? 'id', 'bigIncrements'))
  }

  timestamps(): void {
    const now = new Date().toISOString()
    const createdAt = this.createColumnMeta('created_at', 'datetime')
    createdAt.defaultValue = now
    const updatedAt = this.createColumnMeta('updated_at', 'datetime')
    updatedAt.defaultValue = now
  }
}

/**
 * In-memory alter table builder
 */
class MemoryAlterTableBuilder extends MemoryTableBuilder implements SchemaAlterTableBuilder {
  dropColumn(name: string): void {
    this.tableMeta.columns.delete(name)
  }

  renameColumn(from: string, to: string): void {
    const column = this.tableMeta.columns.get(from)
    if (column) {
      column.name = to
      this.tableMeta.columns.delete(from)
      this.tableMeta.columns.set(to, column)
    }
  }
}

/**
 * In-Memory schema adapter.
 *
 * @example
 * const schema = createInMemorySchemaAdapter()
 *
 * await schema.createTable('users', (t) => {
 *   t.string('id').primary()
 *   t.string('email').unique().notNullable()
 *   t.timestamps()
 * })
 *
 * // Check state
 * console.log(await schema.hasTable('users')) // true
 * console.log(await schema.getColumns('users')) // [...]
 *
 * // Clear for test isolation
 * schema.clear()
 */
export class InMemorySchemaAdapter implements SchemaAdapter {
  private tables: Map<string, MemoryTableMeta> = new Map()

  /**
   * Raw access - returns the internal Map (for debugging/testing)
   */
  get raw(): unknown {
    return this.tables
  }

  /**
   * Clear all schema metadata (useful between tests)
   */
  clear(): void {
    this.tables.clear()
  }

  /**
   * Get table metadata (for testing assertions)
   */
  getTableMeta(tableName: string): MemoryTableMeta | undefined {
    return this.tables.get(tableName)
  }

  async hasTable(tableName: string): Promise<boolean> {
    return this.tables.has(tableName)
  }

  async hasColumn(tableName: string, columnName: string): Promise<boolean> {
    const table = this.tables.get(tableName)
    if (!table) return false
    return table.columns.has(columnName)
  }

  async getColumns(tableName: string): Promise<SchemaColumnInfo[]> {
    const table = this.tables.get(tableName)
    if (!table) return []

    return Array.from(table.columns.values()).map(col => ({
      name: col.name,
      type: col.type,
      nullable: col.nullable,
      defaultValue: col.defaultValue
    }))
  }

  async createTable(tableName: string, builder: (t: SchemaTableBuilder) => void): Promise<void> {
    if (this.tables.has(tableName)) {
      throw new Error(`Table "${tableName}" already exists`)
    }

    const tableMeta: MemoryTableMeta = {
      name: tableName,
      columns: new Map()
    }

    builder(new MemoryTableBuilder(tableMeta))
    this.tables.set(tableName, tableMeta)
  }

  async alterTable(tableName: string, builder: (t: SchemaAlterTableBuilder) => void): Promise<void> {
    const tableMeta = this.tables.get(tableName)
    if (!tableMeta) {
      throw new Error(`Table "${tableName}" not found`)
    }

    builder(new MemoryAlterTableBuilder(tableMeta))
  }

  async dropTable(tableName: string): Promise<void> {
    this.tables.delete(tableName)
  }

  async addTimestamps(tableName: string): Promise<void> {
    const hasCreatedAt = await this.hasColumn(tableName, 'created_at')
    if (hasCreatedAt) return

    await this.alterTable(tableName, (t) => {
      t.datetime('created_at').defaultTo(new Date().toISOString())
      t.datetime('updated_at').defaultTo(new Date().toISOString())
    })
  }

  async addAuditFields(tableName: string): Promise<void> {
    const hasCreatedBy = await this.hasColumn(tableName, 'created_by')
    if (hasCreatedBy) return

    await this.alterTable(tableName, (t) => {
      t.string('created_by').nullable()
      t.string('updated_by').nullable()
    })
  }

  async addColumnIfMissing(tableName: string, columnName: string, type: string): Promise<boolean> {
    const exists = await this.hasColumn(tableName, columnName)
    if (exists) return false

    await this.alterTable(tableName, (t) => {
      switch (type) {
        case 'string':
          t.string(columnName).nullable()
          break
        case 'text':
          t.text(columnName).nullable()
          break
        case 'integer':
          t.integer(columnName).nullable()
          break
        case 'boolean':
          t.boolean(columnName).nullable()
          break
        case 'date':
          t.date(columnName).nullable()
          break
        case 'datetime':
          t.datetime(columnName).nullable()
          break
        case 'json':
          t.json(columnName).nullable()
          break
        case 'uuid':
          t.uuid(columnName).nullable()
          break
        default:
          t.string(columnName).nullable()
      }
    })
    return true
  }
}

/**
 * Create an InMemorySchemaAdapter instance.
 *
 * @returns SchemaAdapter implementation backed by memory
 */
export function createInMemorySchemaAdapter(): InMemorySchemaAdapter {
  return new InMemorySchemaAdapter()
}
